import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Brain, Loader2, Sparkles, Mail, Calendar, TrendingUp,
  CheckCircle, Award, BookOpen, RefreshCw, Send
} from 'lucide-react';
import { toast } from 'sonner';

export default function WeeklyReportsPage() {
  const { api } = useAuth();
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [sending, setSending] = useState(null);
  const [reports, setReports] = useState({});

  const fetchLearners = useCallback(async () => {
    try {
      const res = await api.get('/progress-reports/');
      setLearners(res.data || []);
    } catch (e) {
      console.error('Error fetching learner data:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchLearners(); }, [fetchLearners]);

  const generateReport = async (learnerId) => {
    setGenerating(learnerId);
    try {
      const res = await api.post('/progress-reports/generate/', { learner_id: learnerId });
      setReports(prev => ({ ...prev, [learnerId]: res.data }));
      toast.success('Report generated!');
    } catch (e) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(null);
    }
  };

  const sendEmail = async (learnerId) => {
    const report = reports[learnerId];
    if (!report?.report) { toast.error('Generate report first'); return; }
    setSending(learnerId);
    try {
      await api.post('/progress-reports/send-email/', {
        learner_id: learnerId,
        report: report.report,
      });
      toast.success('Report emailed to you!');
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="reports-loading">
        <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="weekly-reports-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Brain className="h-6 w-6 text-indigo-600" />
          Weekly Progress Reports
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          AI-powered personalized summaries of your child's STEM journey — with recommendations
        </p>
      </div>

      {learners.length === 0 ? (
        <Card className="border-dashed border-2" data-testid="no-learners">
          <CardContent className="p-12 text-center">
            <Brain className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-700 mb-2">No Learners Found</h3>
            <p className="text-sm text-gray-500">Add learners to your account to generate progress reports.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {learners.map((learner) => {
            const report = reports[learner.learner_id];
            const data = learner.data;
            const isGenerating = generating === learner.learner_id;
            const isSending = sending === learner.learner_id;

            return (
              <Card key={learner.learner_id} className="overflow-hidden" data-testid={`report-card-${learner.learner_id}`}>
                <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{learner.learner_name}</CardTitle>
                      <p className="text-sm text-gray-500">Grade {learner.grade}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => generateReport(learner.learner_id)}
                        disabled={isGenerating}
                        data-testid={`generate-btn-${learner.learner_id}`}
                      >
                        {isGenerating ? (
                          <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Generating...</>
                        ) : (
                          <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {report ? 'Refresh' : 'Generate Report'}</>
                        )}
                      </Button>
                      {report && (
                        <Button
                          size="sm"
                          onClick={() => sendEmail(learner.learner_id)}
                          disabled={isSending}
                          className="bg-rsc-navy"
                          data-testid={`email-btn-${learner.learner_id}`}
                        >
                          {isSending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          ) : (
                            <Mail className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Email Report
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-blue-700">{data.total_points}</p>
                      <p className="text-[10px] text-blue-500 uppercase">Total Points</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-green-700">{data.overall_attendance_rate}</p>
                      <p className="text-[10px] text-green-500 uppercase">Attendance</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-purple-700">{data.courses?.length || 0}</p>
                      <p className="text-[10px] text-purple-500 uppercase">Courses</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-amber-700">+{data.points_earned_this_week}</p>
                      <p className="text-[10px] text-amber-500 uppercase">Pts This Week</p>
                    </div>
                  </div>

                  {/* Course List */}
                  {data.courses?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Enrolled Courses</h4>
                      <div className="space-y-1.5">
                        {data.courses.map((c, i) => (
                          <div key={i} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-3.5 w-3.5 text-gray-400" />
                              <span className="font-medium text-gray-700">{c.title}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{c.category}</Badge>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{c.sessions_attended}/{c.total_sessions} sessions</span>
                              <Badge className={c.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                                {c.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Badges & Certs this week */}
                  {(data.new_badges?.length > 0 || data.new_certificates?.length > 0) && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {data.new_badges?.map((b, i) => (
                        <Badge key={i} className="bg-amber-100 text-amber-700">
                          <Award className="h-3 w-3 mr-1" /> {b}
                        </Badge>
                      ))}
                      {data.new_certificates?.map((c, i) => (
                        <Badge key={i} className="bg-indigo-100 text-indigo-700">
                          <CheckCircle className="h-3 w-3 mr-1" /> {c.track} ({c.grade})
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* AI Report */}
                  {report ? (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100" data-testid={`ai-report-${learner.learner_id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-indigo-500" />
                        <span className="text-xs font-semibold text-indigo-600 uppercase">AI-Generated Progress Report</span>
                        <span className="text-[10px] text-gray-400 ml-auto">
                          {new Date(report.generated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                        {report.report}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200" data-testid={`no-report-${learner.learner_id}`}>
                      <Sparkles className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click "Generate Report" to create a personalized AI progress summary</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
